import { useMemo, useState } from 'react';
import { Typography } from '@mui/material';

// Our Components
import DataTable from 'components/DataTable/DataTable';
import ColumnItem from 'components/DataTable/ColumnItem';
import Modal from 'components/Modal/Modal';
import PylonDetailedRates from 'components/DataTable/rates/PylonDetailedRates';

// our utils
import { dollarFormatter } from 'shared/utils';

function RateComparisonTable({ mortechRates }) {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [selectedRate, setSelectedRate] = useState(null);

	const cachedRows = useMemo(() => {
		if (!mortechRates) return [];

		return mortechRates;
	}, [mortechRates]);

	const lenderName = 'lenderName';
	const propertyValue = 'propertyValue';
	const totalLoanAmount = 'totalLoanAmount';
	const APR = 'apr';
	const noteRate = 'noteRate';
	const points = 'pointsPaid';
	const fees = 'fees';
	const monthlyPayment = 'monthlyPayment';
	const cashToClose = 'cashToClose';
	const type = 'type';

	const columns = useMemo(() => {
		return [
			ColumnItem(type, 'Source', 1, {
				valueFormatter: ({ value }) => {
					if (value.includes('mortech')) return 'Mortech';
					return value;
				}
			}),
			ColumnItem(lenderName, 'Lender Name', 2),
			ColumnItem(propertyValue, 'Property Value', 2, {
				valueFormatter: ({ value }) => {
					if (value === 0) return 'N/A';
					return dollarFormatter.format(value);
				}
			}),
			ColumnItem(totalLoanAmount, 'Total Loan Amount', 2, {
				valueFormatter: ({ value }) => {
					return dollarFormatter.format(value);
				}
			}),
			ColumnItem(APR, 'APR', 2, {
				valueFormatter: ({ value }) => {
					return `${value}%`;
				}
			}),
			ColumnItem(noteRate, 'Note Rate', 2, {
				valueFormatter: ({ value }) => {
					return `${value}%`;
				}
			}),
			ColumnItem(points, 'Points Paid', 2),
			ColumnItem(fees, 'Fees', 2, {
				valueFormatter: ({ value }) => {
					return dollarFormatter.format(value);
				}
			}),
			ColumnItem(monthlyPayment, 'Monthly Payment', 2, {
				valueFormatter: ({ value }) => {
					return dollarFormatter.format(value);
				}
			}),
			ColumnItem(cashToClose, 'Cash to Close', 2, {
				valueFormatter: ({ value }) => {
					return dollarFormatter.format(value);
				},
				renderCell: (params) => {
					const { value, formattedValue } = params;
					const isPylon = params.row.type.includes('Pylon');
					if (!isPylon) return formattedValue;

					return value > 0 ? (
						<Typography color="green" variant="overline2">
							{dollarFormatter.format(Math.abs(value))}
						</Typography>
					) : (
						dollarFormatter.format(Math.abs(value))
					);
				}
			})
		];
	}, []);

	return (
		<>
			<DataTable
				rows={cachedRows}
				columns={columns}
				handleRowClick={(row) => {
					const isPylonRate = row.type.includes('Pylon');
					if (isPylonRate) {
						setSelectedRate(row);
						setIsModalOpen(true);
					}
				}}
			/>
			{isModalOpen && (
				<Modal
					fullScreen
					title={`${selectedRate?.lenderName} Product Rates`}
					subtitle={`${selectedRate?.term} Year Fixed`}
					isOpen={isModalOpen}
					handleClose={() => {
						setIsModalOpen(false);
						setSelectedRate(null);
					}}
				>
					<PylonDetailedRates selectedRate={selectedRate} />
				</Modal>
			)}
		</>
	);
}

export default RateComparisonTable;
